import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LayoutGrid, Package, Upload, KeyRound, LogOut, Trash2, Plus, Loader2,
  Download, Cloud, Database, ExternalLink, X, Layers, CreditCard,
} from "lucide-react";
import { toast } from "sonner";
import { api, mediaUrl, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LogoMark } from "@/components/Logo";
import PaymentsPanel from "@/components/PaymentsPanel";

const CATS = ["Tools", "Games", "Social", "Media", "Productivity", "Launchers", "System", "Utilities"];
const TABS = [
  { id: "overview", label: "Overview", icon: LayoutGrid },
  { id: "apps", label: "Apps", icon: Package },
  { id: "upload", label: "Upload APK", icon: Upload },
  { id: "payments", label: "Payments", icon: CreditCard },
  { id: "credentials", label: "Credentials", icon: KeyRound },
];

const fmtSize = (b) => (!b ? "—" : b < 1024 * 1024 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`);

function Field({ label, children }) {
  return (
    <div>
      <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-wider text-slate-500">{label}</label>
      {children}
    </div>
  );
}
const inputCls = "w-full rounded-lg border border-[#1E2433] bg-[#0b0d13] px-3 py-2 text-sm text-slate-200 outline-none transition-colors focus:border-emerald-500/60";

function StorageToggle({ value, onChange }) {
  return (
    <div className="flex gap-2">
      {[
        { v: "cloud", icon: Cloud, label: "Cloud storage", desc: "Scalable object store" },
        { v: "db", icon: Database, label: "DB vault", desc: "Stored in database" },
      ].map((o) => (
        <button
          key={o.v}
          type="button"
          data-testid={`storage-${o.v}`}
          onClick={() => onChange(o.v)}
          className={`flex flex-1 items-center gap-2 rounded-lg border p-3 text-left transition-all ${
            value === o.v ? "border-emerald-500/60 bg-emerald-500/10" : "border-[#1E2433] hover:border-slate-600"
          }`}
        >
          <o.icon className={`h-5 w-5 ${value === o.v ? "text-emerald-400" : "text-slate-500"}`} />
          <div>
            <p className="text-xs font-semibold text-slate-200">{o.label}</p>
            <p className="text-[10px] text-slate-500">{o.desc}</p>
          </div>
        </button>
      ))}
    </div>
  );
}

function UploadForm({ onDone }) {
  const empty = { name: "", package_id: "", developer: "", category: "Tools", description: "", rating: "4.5", version: "1.0.0", changelog: "", featured: false };
  const [f, setF] = useState(empty);
  const [storageMode, setStorageMode] = useState("cloud");
  const [apk, setApk] = useState(null);
  const [icon, setIcon] = useState(null);
  const [shots, setShots] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!apk) return toast.error("Please attach an .apk file");
    setBusy(true);
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, v));
    fd.append("storage_mode", storageMode);
    fd.append("apk", apk);
    if (icon) fd.append("icon", icon);
    shots.forEach((s) => fd.append("screenshots", s));
    try {
      await api.post("/admin/apps", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${f.name} published`);
      setF(empty); setApk(null); setIcon(null); setShots([]);
      onDone();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5 rounded-xl border border-[#1E2433] bg-[#10131B] p-5" data-testid="upload-form">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="App name"><input data-testid="f-name" required className={inputCls} value={f.name} onChange={(e) => set("name", e.target.value)} /></Field>
        <Field label="Package ID"><input data-testid="f-package" required placeholder="com.example.app" className={inputCls} value={f.package_id} onChange={(e) => set("package_id", e.target.value)} /></Field>
        <Field label="Developer"><input data-testid="f-developer" required className={inputCls} value={f.developer} onChange={(e) => set("developer", e.target.value)} /></Field>
        <Field label="Category">
          <select data-testid="f-category" className={inputCls} value={f.category} onChange={(e) => set("category", e.target.value)}>
            {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Version"><input data-testid="f-version" required placeholder="1.0.0" className={inputCls} value={f.version} onChange={(e) => set("version", e.target.value)} /></Field>
        <Field label="Rating (0-5)"><input data-testid="f-rating" type="number" step="0.1" min="0" max="5" className={inputCls} value={f.rating} onChange={(e) => set("rating", e.target.value)} /></Field>
      </div>
      <Field label="Description"><textarea data-testid="f-description" rows={3} className={inputCls} value={f.description} onChange={(e) => set("description", e.target.value)} /></Field>
      <Field label="Changelog (this version)"><textarea data-testid="f-changelog" rows={2} className={inputCls} value={f.changelog} onChange={(e) => set("changelog", e.target.value)} /></Field>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Field label="APK file (.apk)"><input data-testid="f-apk" type="file" accept=".apk,application/vnd.android.package-archive" onChange={(e) => setApk(e.target.files[0])} className="w-full text-xs text-slate-400 file:mr-2 file:rounded file:border-0 file:bg-emerald-500/20 file:px-2 file:py-1.5 file:text-emerald-300" /></Field>
        <Field label="Icon (image)"><input data-testid="f-icon" type="file" accept="image/*" onChange={(e) => setIcon(e.target.files[0])} className="w-full text-xs text-slate-400 file:mr-2 file:rounded file:border-0 file:bg-slate-700 file:px-2 file:py-1.5 file:text-slate-200" /></Field>
        <Field label="Screenshots (multi)"><input data-testid="f-shots" type="file" accept="image/*" multiple onChange={(e) => setShots(Array.from(e.target.files))} className="w-full text-xs text-slate-400 file:mr-2 file:rounded file:border-0 file:bg-slate-700 file:px-2 file:py-1.5 file:text-slate-200" /></Field>
      </div>

      <Field label="APK storage location"><StorageToggle value={storageMode} onChange={setStorageMode} /></Field>

      <label className="flex items-center gap-2 text-sm text-slate-300">
        <input data-testid="f-featured" type="checkbox" checked={f.featured} onChange={(e) => set("featured", e.target.checked)} className="h-4 w-4 accent-emerald-400" />
        Feature on store homepage
      </label>

      <button data-testid="upload-submit" disabled={busy} className="co-btn-primary flex w-full items-center justify-center gap-2 rounded-lg py-2.5 text-sm disabled:opacity-60">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
        {busy ? "Publishing…" : "Publish app"}
      </button>
    </form>
  );
}

function VersionModal({ app, onClose, onDone }) {
  const [version, setVersion] = useState("");
  const [changelog, setChangelog] = useState("");
  const [storageMode, setStorageMode] = useState("cloud");
  const [apk, setApk] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!apk) return toast.error("Attach an .apk file");
    setBusy(true);
    const fd = new FormData();
    fd.append("version", version);
    fd.append("changelog", changelog);
    fd.append("storage_mode", storageMode);
    fd.append("apk", apk);
    try {
      await api.post(`/admin/apps/${app.id}/versions`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`v${version} added to ${app.name}`);
      onDone();
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm" onClick={onClose}>
      <motion.form
        initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
        onClick={(e) => e.stopPropagation()} onSubmit={submit}
        data-testid="version-modal"
        className="w-full max-w-md space-y-4 rounded-2xl border border-[#1E2433] bg-[#10131B] p-6"
      >
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl font-bold text-slate-100">New version — {app.name}</h3>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-200"><X className="h-5 w-5" /></button>
        </div>
        <Field label="Version"><input data-testid="v-version" required placeholder="1.1.0" className={inputCls} value={version} onChange={(e) => setVersion(e.target.value)} /></Field>
        <Field label="Changelog"><textarea data-testid="v-changelog" rows={2} className={inputCls} value={changelog} onChange={(e) => setChangelog(e.target.value)} /></Field>
        <Field label="APK file"><input data-testid="v-apk" type="file" accept=".apk" onChange={(e) => setApk(e.target.files[0])} className="w-full text-xs text-slate-400 file:mr-2 file:rounded file:border-0 file:bg-emerald-500/20 file:px-2 file:py-1.5 file:text-emerald-300" /></Field>
        <Field label="Storage"><StorageToggle value={storageMode} onChange={setStorageMode} /></Field>
        <button data-testid="v-submit" disabled={busy} className="co-btn-primary flex w-full items-center justify-center gap-2 rounded-lg py-2.5 text-sm disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add version
        </button>
      </motion.form>
    </div>
  );
}

function CredentialsForm() {
  const { setToken } = useAuth();
  const [cur, setCur] = useState("");
  const [u, setU] = useState("");
  const [p, setP] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!u && !p) return toast.error("Enter a new username or password");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-credentials", {
        current_password: cur,
        new_username: u || null,
        new_password: p || null,
      });
      setToken(data.token, data.username);
      toast.success("Credentials updated");
      setCur(""); setU(""); setP("");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="max-w-md space-y-4 rounded-xl border border-[#1E2433] bg-[#10131B] p-5" data-testid="credentials-form">
      <p className="text-xs text-slate-500">Update your admin username and/or password. Confirm with your current password.</p>
      <Field label="Current password"><input data-testid="c-current" type="password" required className={inputCls} value={cur} onChange={(e) => setCur(e.target.value)} /></Field>
      <Field label="New username (optional)"><input data-testid="c-username" className={inputCls} value={u} onChange={(e) => setU(e.target.value)} /></Field>
      <Field label="New password (optional, min 6)"><input data-testid="c-password" type="password" className={inputCls} value={p} onChange={(e) => setP(e.target.value)} /></Field>
      <button data-testid="c-submit" disabled={busy} className="co-btn-primary flex items-center justify-center gap-2 rounded-lg px-5 py-2.5 text-sm disabled:opacity-60">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} Update credentials
      </button>
    </form>
  );
}

export default function AdminDashboard() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [apps, setApps] = useState([]);
  const [stats, setStats] = useState(null);
  const [versionApp, setVersionApp] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(async () => {
    const [a, s] = await Promise.all([api.get("/apps"), api.get("/admin/stats")]);
    setApps(a.data);
    setStats(s.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const del = async (app) => {
    try {
      await api.delete(`/admin/apps/${app.id}`);
      toast.success(`${app.name} deleted`);
      setConfirmDel(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="min-h-screen bg-[#08090E]">
      <header className="sticky top-0 z-40 border-b border-[#1E2433]/80 bg-[#08090E]/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-3 sm:px-8">
          <LogoMark size="sm" />
          <div>
            <p className="font-display text-lg font-extrabold leading-none text-slate-100">crazyOne <span className="text-emerald-400">Portal</span></p>
            <p className="font-mono text-[10px] text-slate-500">signed in as {admin?.username}</p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button data-testid="view-store" onClick={() => navigate("/")} className="flex items-center gap-1.5 rounded-lg border border-[#1E2433] px-3 py-1.5 text-xs text-slate-300 hover:border-slate-600">
              <ExternalLink className="h-3.5 w-3.5" /> Store
            </button>
            <button data-testid="logout-btn" onClick={logout} className="flex items-center gap-1.5 rounded-lg border border-red-500/30 px-3 py-1.5 text-xs text-red-400 hover:bg-red-500/10">
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-4 py-6 sm:px-8">
        <div className="mb-6 flex gap-2 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex shrink-0 items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium transition-all ${
                tab === t.id ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-300" : "border-[#1E2433] text-slate-400 hover:border-slate-600 hover:text-slate-200"
              }`}
            >
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "overview" && (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {[
              { label: "Total apps", value: stats?.total_apps ?? 0, icon: Package },
              { label: "Versions", value: stats?.total_versions ?? 0, icon: Layers },
              { label: "Downloads", value: stats?.total_downloads ?? 0, icon: Download },
              { label: "Categories", value: stats ? Object.keys(stats.by_category).length : 0, icon: LayoutGrid },
            ].map((m, i) => (
              <div key={i} data-testid={`stat-${i}`} className="rounded-xl border border-[#1E2433] bg-[#10131B] p-5">
                <m.icon className="h-5 w-5 text-emerald-400" />
                <p className="font-display mt-3 text-3xl font-extrabold text-slate-100">{m.value}</p>
                <p className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{m.label}</p>
              </div>
            ))}
          </div>
        )}

        {tab === "apps" && (
          <div className="space-y-3" data-testid="apps-list">
            {apps.length === 0 && <p className="py-10 text-center text-sm text-slate-500">No apps yet. Upload one from the Upload tab.</p>}
            {apps.map((app) => (
              <div key={app.id} data-testid={`admin-app-${app.slug}`} className="flex flex-wrap items-center gap-4 rounded-xl border border-[#1E2433] bg-[#10131B] p-4">
                <div className="h-12 w-12 overflow-hidden rounded-lg border border-[#1E2433] bg-[#0b0d13]">
                  {mediaUrl(app.icon_path) ? <img src={mediaUrl(app.icon_path)} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full w-full items-center justify-center font-display text-xl font-black text-emerald-300">{app.name?.[0]}</div>}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-display text-lg font-bold text-slate-100">{app.name} {app.featured && <span className="ml-1 rounded border border-emerald-500/30 px-1.5 py-0.5 font-mono text-[9px] uppercase text-emerald-400">featured</span>}</p>
                  <p className="font-mono text-xs text-slate-500">{app.category} · v{app.current_version} · {fmtSize(app.current_size)} · {app.downloads} dl</p>
                </div>
                <div className="flex gap-2">
                  <button data-testid={`add-version-${app.slug}`} onClick={() => setVersionApp(app)} className="flex items-center gap-1.5 rounded-lg border border-emerald-500/40 px-3 py-1.5 text-xs text-emerald-300 hover:bg-emerald-500/10"><Plus className="h-3.5 w-3.5" /> Version</button>
                  <button data-testid={`delete-app-${app.slug}`} onClick={() => setConfirmDel(app)} className="flex items-center gap-1.5 rounded-lg border border-red-500/30 px-3 py-1.5 text-xs text-red-400 hover:bg-red-500/10"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "upload" && <UploadForm onDone={() => { load(); setTab("apps"); }} />}
        {tab === "payments" && <PaymentsPanel />}
        {tab === "credentials" && <CredentialsForm />}
      </div>

      {versionApp && <VersionModal app={versionApp} onClose={() => setVersionApp(null)} onDone={load} />}

      {confirmDel && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm" onClick={() => setConfirmDel(null)}>
          <div onClick={(e) => e.stopPropagation()} data-testid="delete-confirm" className="w-full max-w-sm rounded-2xl border border-[#1E2433] bg-[#10131B] p-6 text-center">
            <Trash2 className="mx-auto h-8 w-8 text-red-400" />
            <p className="font-display mt-3 text-xl font-bold text-slate-100">Delete {confirmDel.name}?</p>
            <p className="mt-1 text-sm text-slate-500">This removes the app and all its versions permanently.</p>
            <div className="mt-5 flex gap-3">
              <button onClick={() => setConfirmDel(null)} className="flex-1 rounded-lg border border-[#1E2433] py-2 text-sm text-slate-300 hover:border-slate-600">Cancel</button>
              <button data-testid="confirm-delete-btn" onClick={() => del(confirmDel)} className="flex-1 rounded-lg bg-red-500 py-2 text-sm font-semibold text-white hover:bg-red-600">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
